import {
  type CurrencyCode,
  getCurrencyScale,
  isCurrencyCode,
} from './currency';
import {
  DEFAULT_ROUNDING,
  divRound,
  pow10,
  type RoundingMode,
} from './rounding';

/**
 * Exact, currency-aware monetary value stored as an integer bigint count
 * of minor units ("cents"). Immutable.
 *
 *   USD 12.34  -> { amount: 1234n,  currency: 'USD', scale: 2 }
 *   JPY 345    -> { amount: 345n,   currency: 'JPY', scale: 0 }
 *   BHD 1.234  -> { amount: 1234n,  currency: 'BHD', scale: 3 }
 *
 * Never convert through `Number` for math. Use the `Money.*` helpers
 * exported from this module.
 */
export interface Money {
  readonly amount: bigint;
  readonly currency: CurrencyCode;
  readonly scale: number;
}

/**
 * Brand check used by {@link isMoney}. Plain object literals are still
 * valid `Money` because we want serialisation round-trips to just work.
 */
export function isMoney(value: unknown): value is Money {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['amount'] === 'bigint' &&
    typeof v['currency'] === 'string' &&
    isCurrencyCode(v['currency']) &&
    typeof v['scale'] === 'number'
  );
}

function make(amount: bigint, currency: CurrencyCode): Money {
  return Object.freeze({ amount, currency, scale: getCurrencyScale(currency) });
}

function assertSameCurrency(a: Money, b: Money, op: string): void {
  if (a.currency !== b.currency) {
    throw new Error(
      `Money.${op}: currency mismatch — got ${a.currency} vs ${b.currency}. ` +
        `FX conversion is not a domain primitive.`,
    );
  }
}

/**
 * Convert a user-facing input (bigint minor units, number major units, or
 * string major units) into a {@link Money} under the given currency.
 *
 * - `bigint` is treated as minor units verbatim (no scaling).
 * - `number` is treated as a major-unit amount. Only finite, non-NaN
 *   values with ≤ `scale` fractional digits are accepted — otherwise
 *   `from` throws rather than silently rounding.
 * - `string` is treated as a **major-unit decimal** ("12.34"). This
 *   overload is **deprecated** because the same shape ("100000") is
 *   indistinguishable from a wire-format minor-units string at the
 *   call site, and getting it wrong has caused production bugs (see
 *   [docs/02-money.md → "Wire vs decimal"](../../docs/02-money.md)).
 *   Use {@link fromMinor} for wire amounts and {@link parseMoneyInput}
 *   for user-typed input.
 *
 * Three signatures are declared so TypeScript can flag the deprecated
 * one (string) without dimming the other two — the wide overload at
 * the bottom is the implementation signature and should never be
 * called from outside this file.
 */
/**
 * @deprecated Strings are ambiguous — `Money.of('100000', 'USD')` is
 * $100,000.00 but reads at the call site like $1,000.00 in cents.
 * Use `Money.fromMinor('100000', 'USD')` for wire amounts (DB / API
 * minor-units strings) or `parseMoneyInput('100,000', 'USD')` for
 * user input. Hard-coded decimal literals in test fixtures may
 * continue to use this overload behind a file-scoped
 * `eslint-disable @typescript-eslint/no-deprecated`.
 */
function of(amount: string, currency: CurrencyCode): Money;
function of(amount: bigint | number, currency: CurrencyCode): Money;
function of(amount: bigint | number | string, currency: CurrencyCode): Money;
function of(amount: bigint | number | string, currency: CurrencyCode): Money {
  const scale = getCurrencyScale(currency);

  if (typeof amount === 'bigint') {
    return make(amount, currency);
  }

  if (typeof amount === 'number') {
    if (!Number.isFinite(amount)) {
      throw new Error(`Money.of: amount must be finite, got ${String(amount)}`);
    }
    // Stringify with enough precision to cover the currency's scale and
    // reject anything that can't be represented exactly.
    return fromString(amount.toFixed(scale + 1), currency);
  }

  if (typeof amount === 'string') {
    return fromString(amount, currency);
  }

  throw new Error(`Money.of: unsupported amount type ${typeof amount}`);
}

/**
 * Lift a **wire-format minor-units** amount into a {@link Money}.
 *
 * This is the canonical entry point for any monetary value that came
 * off the wire / database — `totals.amountDue`, `payments.amount`
 * aggregates, `(totals ->> 'amountPaid')::numeric`, etc. The amount
 * is the integer count of minor units:
 *
 *   `Money.fromMinor('100000', 'USD')` → $1,000.00 (100,000 cents)
 *   `Money.fromMinor('1234',  'USD')`  → $12.34   ( 1,234 cents)
 *   `Money.fromMinor('1234',  'JPY')`  → ¥1,234   ( 1,234 yen)
 *   `Money.fromMinor( 1234n,  'USD')`  → $12.34
 *
 * Why this exists separately from {@link of}: `Money.of('100000',
 * 'USD')` reads as "$100,000" because the string overload of `of`
 * parses *decimals*. That ambiguity caused the 2026-05-04 production
 * bug where AI-drafted reminders quoted customers 100x the actual
 * invoice. `fromMinor` is unambiguous at the call site — anyone
 * reading `Money.fromMinor(amountDue, currency)` knows the input is
 * in minor units, full stop.
 *
 * Throws when the string isn't an integer ("12.34", "abc", "" all
 * reject) — wire amounts are always integer minor units by
 * construction, so a non-integer here means the caller is using the
 * wrong helper.
 */
function fromMinor(amount: bigint | string, currency: CurrencyCode): Money {
  if (typeof amount === 'bigint') {
    return make(amount, currency);
  }
  const trimmed = amount.trim();
  if (!/^-?\d+$/.test(trimmed)) {
    throw new Error(
      `Money.fromMinor: expected an integer string of minor units, got "${amount}". ` +
        'Use Money.of(decimalString) for decimal strings, or parseMoneyInput for user input.',
    );
  }
  return make(BigInt(trimmed), currency);
}

/**
 * Strict major-unit string parser. Only accepts trailing zeros beyond the
 * currency scale; any non-zero excess digit is rejected rather than
 * silently rounded. Callers who want locale-aware parsing or rounding
 * should use {@link parseMoneyInput} or {@link Money.mul}/{@link Money.div}.
 */
function fromString(raw: string, currency: CurrencyCode): Money {
  const scale = getCurrencyScale(currency);
  const trimmed = raw.trim();

  // Accept either `[-+]?\d+(\.\d+)?` or a leading-dot form like `.5`.
  const match = /^(-|\+)?(?:(\d+)(?:\.(\d+))?|\.(\d+))$/.exec(trimmed);
  if (!match) {
    throw new Error(`Money.of: cannot parse "${raw}" as ${currency}`);
  }

  const [, sign, whole, frac1, frac2] = match;
  const wholePart = whole ?? '';
  const frac = frac1 ?? frac2 ?? '';
  let fracPart: string = frac;

  if (fracPart.length > scale) {
    const excess = fracPart.slice(scale);
    if (!/^0+$/.test(excess)) {
      throw new Error(
        `Money.of: "${raw}" has more fractional digits than ${currency} supports (scale=${scale})`,
      );
    }
    fracPart = fracPart.slice(0, scale);
  } else if (fracPart.length < scale) {
    fracPart = fracPart.padEnd(scale, '0');
  }

  const digits = `${wholePart}${fracPart}`;
  const magnitude = digits === '' ? 0n : BigInt(digits);
  return make(sign === '-' ? -magnitude : magnitude, currency);
}

function zero(currency: CurrencyCode): Money {
  return make(0n, currency);
}

function add(a: Money, b: Money): Money {
  assertSameCurrency(a, b, 'add');
  return make(a.amount + b.amount, a.currency);
}

function sub(a: Money, b: Money): Money {
  assertSameCurrency(a, b, 'sub');
  return make(a.amount - b.amount, a.currency);
}

function sum(parts: readonly Money[], currency?: CurrencyCode): Money {
  if (parts.length === 0) {
    if (!currency) {
      throw new Error('Money.sum: currency must be provided when parts is empty');
    }
    return zero(currency);
  }
  const head = parts[0]!;
  let total = head.amount;
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i]!;
    assertSameCurrency(head, part, 'sum');
    total += part.amount;
  }
  return make(total, head.currency);
}

function negate(a: Money): Money {
  return make(-a.amount, a.currency);
}

function abs(a: Money): Money {
  return make(a.amount < 0n ? -a.amount : a.amount, a.currency);
}

/**
 * Multiply a Money amount by a scalar (integer bigint, integer number,
 * or decimal string such as `"0.075"` for a 7.5 % rate).
 *
 * `number` inputs are rejected when they aren't finite or contain
 * non-representable precision — the caller must stringify instead.
 */
function mul(
  a: Money,
  scalar: bigint | number | string,
  mode: RoundingMode = DEFAULT_ROUNDING,
): Money {
  const { num, den } = scalarToRatio(scalar);
  // a.amount * num may be huge but bigint handles arbitrary precision.
  const product = a.amount * num;
  const rounded = divRound(product, den, mode);
  return make(rounded, a.currency);
}

/**
 * Divide a Money amount by a scalar (same input shapes as {@link mul}).
 * Throws on zero.
 */
function div(
  a: Money,
  scalar: bigint | number | string,
  mode: RoundingMode = DEFAULT_ROUNDING,
): Money {
  const { num, den } = scalarToRatio(scalar);
  if (num === 0n) {
    throw new Error('Money.div: cannot divide by zero');
  }
  const rounded = divRound(a.amount * den, num, mode);
  return make(rounded, a.currency);
}

function scalarToRatio(scalar: bigint | number | string): { num: bigint; den: bigint } {
  if (typeof scalar === 'bigint') {
    return { num: scalar, den: 1n };
  }
  if (typeof scalar === 'number') {
    if (!Number.isFinite(scalar)) {
      throw new Error(`Money: scalar must be finite, got ${String(scalar)}`);
    }
    if (Number.isInteger(scalar)) {
      return { num: BigInt(scalar), den: 1n };
    }
    // Stringify with full precision and reparse.
    return scalarStringToRatio(scalar.toString());
  }
  if (typeof scalar === 'string') {
    return scalarStringToRatio(scalar);
  }
  throw new Error(`Money: unsupported scalar type ${typeof scalar}`);
}

function scalarStringToRatio(raw: string): { num: bigint; den: bigint } {
  const trimmed = raw.trim();
  // Allow a single leading +/- and a single decimal point.
  const match = /^(-|\+)?(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/.exec(trimmed);
  if (!match) {
    throw new Error(`Money: cannot parse scalar "${raw}"`);
  }
  const [, sign, whole, frac = '', expPart] = match;
  // `whole` is a required capture group in the regex; the `?? ''` is
  // only there to satisfy TS tuple narrowing.
  const wholePart = whole ?? '';
  const exp = expPart ? parseInt(expPart, 10) : 0;
  const digits = `${wholePart}${frac}`;
  let num = BigInt(digits);
  let den = pow10(frac.length);
  if (exp > 0) num *= pow10(exp);
  else if (exp < 0) den *= pow10(-exp);
  if (sign === '-') num = -num;
  return { num, den };
}

/**
 * Split `a` into `ratios.length` Money amounts whose sum equals `a`.
 * Remainder cents from integer division are distributed one at a time,
 * largest-fractional-share first (same rule as Fowler's "allocate").
 *
 * Ratios may be integers, floats, or bigints. Negative ratios are not
 * allowed. `allocate` never loses or invents cents.
 */
function allocate(a: Money, ratios: readonly (number | bigint)[]): Money[] {
  if (ratios.length === 0) {
    throw new Error('Money.allocate: ratios must not be empty');
  }
  // Normalise ratios to a bigint numerator + shared denominator so no
  // precision is lost.
  const normalised = ratios.map((r, idx) => {
    if (typeof r === 'bigint') {
      if (r < 0n) throw new Error('Money.allocate: ratios must be non-negative');
      return { num: r, den: 1n, idx };
    }
    if (!Number.isFinite(r) || r < 0) {
      throw new Error(`Money.allocate: ratio at index ${idx} is invalid (${r})`);
    }
    const ratio = scalarStringToRatio(r.toString());
    return { num: ratio.num, den: ratio.den, idx };
  });

  const commonDen = normalised.reduce((d, r) => d * r.den, 1n);
  const scaled = normalised.map((r) => ({ num: (r.num * commonDen) / r.den, idx: r.idx }));
  const total = scaled.reduce((acc, r) => acc + r.num, 0n);

  if (total === 0n) {
    throw new Error('Money.allocate: ratios must sum to > 0');
  }

  const shares: bigint[] = new Array(ratios.length).fill(0n);
  const remainders: { rem: bigint; idx: number }[] = [];
  let distributed = 0n;

  for (const s of scaled) {
    const numerator = a.amount * s.num;
    const quotient = numerator / total; // bigint: truncates toward zero
    shares[s.idx] = quotient;
    distributed += quotient;
    const remainder = numerator - quotient * total;
    remainders.push({ rem: remainder, idx: s.idx });
  }

  let leftover = a.amount - distributed;
  const stepSign = leftover === 0n ? 0n : leftover < 0n ? -1n : 1n;

  if (stepSign !== 0n) {
    // For positive leftovers we want the *largest* remainders first; for
    // negative leftovers (a.amount < 0), the most-negative remainders
    // first. Either way we sort by magnitude descending and step toward
    // zero.
    const ordered = [...remainders].sort((x, y) => {
      const ax = x.rem < 0n ? -x.rem : x.rem;
      const ay = y.rem < 0n ? -y.rem : y.rem;
      if (ay > ax) return 1;
      if (ay < ax) return -1;
      // Stable tiebreaker: original index.
      return x.idx - y.idx;
    });

    for (const entry of ordered) {
      /* v8 ignore next -- defensive: loop terminates via `leftover`. */
      if (leftover === 0n) break;
      // `shares[entry.idx]` is pre-initialised above; `?? 0n` keeps TS
      // happy under `noUncheckedIndexedAccess`.
      shares[entry.idx] = (shares[entry.idx] ?? 0n) + stepSign;
      leftover -= stepSign;
    }
  }

  return shares.map((share) => make(share, a.currency));
}

function eq(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.amount === b.amount;
}

function compare(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b, 'compare');
  if (a.amount < b.amount) return -1;
  if (a.amount > b.amount) return 1;
  return 0;
}

function lt(a: Money, b: Money): boolean {
  return compare(a, b) === -1;
}
function lte(a: Money, b: Money): boolean {
  return compare(a, b) !== 1;
}
function gt(a: Money, b: Money): boolean {
  return compare(a, b) === 1;
}
function gte(a: Money, b: Money): boolean {
  return compare(a, b) !== -1;
}

function isZero(a: Money): boolean {
  return a.amount === 0n;
}
function isPositive(a: Money): boolean {
  return a.amount > 0n;
}
function isNegative(a: Money): boolean {
  return a.amount < 0n;
}

/**
 * Return the major-unit string representation ("1234.56" for USD 1234.56).
 * Locale-agnostic; for user-facing output use {@link formatMoney} instead.
 */
function toDecimalString(a: Money): string {
  const sign = a.amount < 0n ? '-' : '';
  const absAmount = a.amount < 0n ? -a.amount : a.amount;
  if (a.scale === 0) {
    return `${sign}${absAmount.toString()}`;
  }
  const padded = absAmount.toString().padStart(a.scale + 1, '0');
  const whole = padded.slice(0, padded.length - a.scale);
  const frac = padded.slice(padded.length - a.scale);
  return `${sign}${whole}.${frac}`;
}

/**
 * Convert to a JS `number` in major units. LOSSY for large amounts —
 * use only at the display/boundary layer, never for further math.
 */
function toNumberUnsafe(a: Money): number {
  return Number(toDecimalString(a));
}

export const Money = {
  // Re-exporting `of` triggers the no-deprecated rule because the
  // string overload carries the @deprecated tag. The trade-off is
  // worth it: callers writing `Money.of(stringFromDb, currency)`
  // light up at the call site (which is the whole point), and the
  // single-line disable here keeps the central export readable
  // instead of forcing every individual reference to opt out.
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  of,
  fromMinor,
  zero,
  add,
  sub,
  sum,
  mul,
  div,
  allocate,
  negate,
  abs,
  eq,
  compare,
  lt,
  lte,
  gt,
  gte,
  isZero,
  isPositive,
  isNegative,
  toDecimalString,
  toNumberUnsafe,
} as const;

export type { RoundingMode } from './rounding';
